import { useState } from 'react';
import NumberFormat from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';

import {
  DatePickerInput,
  Form,
  PLATFORM_THEME_COLORS,
  NumberInput,
  TextInput,
  Typography,
  SelectInput,
} from '@thinknet-gmbh/platform-common-ui';

import { DEFAULT_NAME_FIELD_REGEX, HealthInsuranceTypesEnum } from '@thinknet-gmbh/platform-common';

import { useGetAllHealthInsurance } from 'src/hooks/health-insurance/get-all-health-insurance.hook';

import { PaymentAdditionalFields } from './payment-additional-fields';
import { AutocompleteSelect } from 'src/components/autocomplete-select';
import { DrawerComponent } from 'src/components/custom-drawer';
import ChangeHealthInsurance from 'src/pages/settings/components/health-insurance/components/change-health-insurance/change-health-insurance.component';

import { ISwitcherDefaultData } from '../../employee.interfaces';

import {
  StyledContainer,
  StyledRowContainer,
  StyledWrapper,
  TitleRowLabel,
} from '../../employee-common.styles';

const PaymentAndInsuranceFields = ({
  isEdit,
  form,
  switcherDefaultData,
  isOwnData,
}: {
  isEdit?: boolean;
  isOwnData?: boolean;
  form: FormInstance;
  switcherDefaultData?: ISwitcherDefaultData;
}) => {
  const { t, i18n } = useTranslation('employee');
  const [activeModal, setActiveModal] = useState<boolean>(false);

  const onToggle = () => {
    setActiveModal(prev => !prev);
  };

  const { healthInsurance } = useGetAllHealthInsurance();

  const healthInsuranceOptions = healthInsurance.map((el: { name: string; _id: string }) => ({
    value: el._id,
    label: el.name,
  }));

  return (
    <div>
      <DrawerComponent
        title={t('settings:healthInsurance.labels.addNewHealthInsurance')}
        subtitle={t('settings:healthInsurance.labels.addNewHealthInsuranceSubtitle')}
        onClose={onToggle}
        visible={activeModal}
        destroyOnClose
      >
        <ChangeHealthInsurance onClose={onToggle} />
      </DrawerComponent>
      <StyledWrapper>
        {!isEdit && (
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('paymentAndInsurance.labels.headerTitle')}
          </TitleRowLabel>
        )}
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.bankInformation')}
          </TitleRowLabel>
          <StyledRowContainer>
            <Form.Item
              name={['paymentAndInsurance', 'bankAccountHolder']}
              label={t('paymentAndInsurance.labels.accountHolder')}
              rules={[
                {
                  required: true,
                  message: t('paymentAndInsurance.validation.accountHolderRequired'),
                },
                {
                  pattern: DEFAULT_NAME_FIELD_REGEX,
                  message: t('common:validation.error.incorrectFormat'),
                },
              ]}
            >
              <TextInput
                maxLength={50}
                placeholder={t('paymentAndInsurance.placeholders.bankAccountHolder')}
              />
            </Form.Item>
            <Form.Item
              name={['paymentAndInsurance', 'iban']}
              label="IBAN"
              validateTrigger={[]}
              rules={[
                { required: true, message: t('paymentAndInsurance.validation.ibanRequired') },
                { len: 22, message: t('paymentAndInsurance.validation.ibanInvalidLength') },
              ]}
            >
              <NumberFormat
                customInput={TextInput}
                prefix="DE"
                isNumericString
                maxLength={22}
                placeholder="DE89370400440532013000"
              />
            </Form.Item>
            <Form.Item
              name={['paymentAndInsurance', 'bic']}
              label="BIC"
              validateTrigger={[]}
              rules={[
                { required: true, message: t('paymentAndInsurance.validation.bicRequired') },
                {
                  pattern: /^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/,
                  message: t('paymentAndInsurance.validation.bicInvalidLength'),
                },
              ]}
            >
              <TextInput maxLength={11} placeholder="AABSDE31" />
            </Form.Item>
          </StyledRowContainer>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.socialSecurity')}
          </TitleRowLabel>
          <StyledRowContainer>
            <Form.Item
              name={['paymentAndInsurance', 'taxId']}
              label={t('paymentAndInsurance.labels.taxId')}
              validateTrigger={[]}
              rules={[
                { required: true, message: t('customers:error.fieldIsRequired') },
                {
                  validator: (_, value) => !value || String(value).length === 11
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('paymentAndInsurance.validation.taxIdInvalidLength'))),
                },
              ]}
            >
              <NumberInput
                disabled={isOwnData}
                maxLength={11}
                minLength={11}
                thousandSeparator={false}
                placeholder={t('paymentAndInsurance.placeholders.taxId')}
              />
            </Form.Item>
            <Form.Item
              name={['paymentAndInsurance', 'nationalInsuranceNumber']}
              label={t('paymentAndInsurance.labels.insuranceNumber')}
              validateTrigger={[]}
              rules={[
                {
                  pattern: /^\d{8}[A-Z]\d{3}$/,
                  message: t('paymentAndInsurance.validation.invalidInsuranceNumber'),
                },
              ]}
            >
              <TextInput
                disabled={isOwnData}
                maxLength={12}
                placeholder="12345678A900"
              />
            </Form.Item>
          </StyledRowContainer>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.insurance')}
          </TitleRowLabel>
          <StyledRowContainer>
            <Form.Item
              name={['paymentAndInsurance', 'healthInsuranceType']}
              label={t('paymentAndInsurance.labels.healthInsuranceType')}
            >
              <SelectInput
                disabled={isOwnData}
                placeholder={t('paymentAndInsurance.labels.insurancePlan')}
              >
                {Object.keys(HealthInsuranceTypesEnum).map(option => (
                  <SelectInput.Option value={option} key={option}>
                    {t(`paymentAndInsurance.labels.healthInsuranceOptions.${option}`)}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
            <div>
              <Form.Item
                name={['paymentAndInsurance', 'healthInsuranceId']}
                label={t('paymentAndInsurance.labels.healthInsuranceName')}
              >
                <AutocompleteSelect
                  disabled={isOwnData}
                  options={healthInsuranceOptions}
                  placeholder="Humana"
                />
              </Form.Item>
              {!isOwnData && (
                <Typography
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={onToggle}
                  fontColor={PLATFORM_THEME_COLORS.primary.blue}
                >
                  {t('settings:healthInsurance.buttons.addHealthInsurance')}
                </Typography>
              )}
            </div>
          </StyledRowContainer>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.residenceStatus')}
          </TitleRowLabel>
          <StyledRowContainer>
            <Form.Item
              name={['paymentAndInsurance', 'typeOfVisa']}
              label={t('paymentAndInsurance.labels.typeOfVisa')}
            >
              <TextInput disabled={isOwnData} placeholder="B-1" maxLength={50} />
            </Form.Item>
            <Form.Item
              name={['paymentAndInsurance', 'visaExpirationDate']}
              label={t('paymentAndInsurance.labels.visaExpiredDate')}
            >
              <DatePickerInput disabled={isOwnData} lang={i18n.language} format="DD.MM.YYYY" />
            </Form.Item>
          </StyledRowContainer>
          <PaymentAdditionalFields
            isEmployeeEditForm={true}
            disabledAll={isOwnData}
            form={form}
            switcherDefaultData={switcherDefaultData}
          />
        </StyledContainer>
      </StyledWrapper>
    </div>
  );
};

export default PaymentAndInsuranceFields;
